/**
 * Lightly normalize Android Phone Number Hint results for OTP login.
 *
 * Does **not** invent a default country from locale (no GB/UK/+44).
 * Digits without `+` are treated as already including a country calling code.
 *
 * Also repairs a common Play Services / locale mangling: when the device region
 * is GB, a number like `919877300253` (India, already includes `91`) is parsed
 * as a UK national number and returned as `+44919877300253`. UK national
 * numbers are at most 10 digits, so a longer "national" part after `44` means
 * the locale prefix was wrongly prepended.
 */
export function normalizePhoneHintValue(raw: string): string {
	const compact = raw.trim().replace(/[^\d+]/g, '');
	if (!compact) {
		return '';
	}

	const hasPlus = compact.startsWith('+');
	let digits = hasPlus ? compact.slice(1) : compact;

	// Reject malformed leftovers such as "++1" or embedded plus signs.
	if (!/^\d{8,15}$/.test(digits)) {
		return '';
	}

	// UK NSN max length is 10. Longer means `44` was prepended onto an
	// already-international number (e.g. 91… India).
	if (digits.startsWith('44') && digits.length - 2 > 10) {
		digits = digits.slice(2);
	}

	return `+${digits}`;
}
